import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../auth/UseUser';
import { ReactComponent as ShowPasswordIcon } from '../../assets/svg/ShowPasswordIcon.svg';
import { ReactComponent as HidePasswordIcon } from '../../assets/svg/HidePasswordIcon.svg';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useState, useRef, useEffect } from 'react';
import Loading from '../Loading';
import { ReactComponent as UpstriveLogo } from '../../assets/svg/UpstriveLogo.svg';
import { login, whoAmI } from '../../server/server';
import CustomLogoIcon from '../../assets/svg/CustomLogoIcon';

type LoginInputs = {
	email: string;
	password: string;
};

export default function SignIn() {
	const navigate = useNavigate();
	const { setUser } = useUser();
	const [showPassword, setShowPassword] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const emailInputRef = useRef<HTMLInputElement | null>(null);
	const passwordInputRef = useRef<HTMLInputElement | null>(null);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<LoginInputs>({
		mode: 'onChange',
		defaultValues: {
			email: '',
			password: '',
		},
	});

	// Focus email input on mount
	useEffect(() => {
		emailInputRef.current?.focus();
	}, []);

	const toggleShowPassword = () => {
		setShowPassword((prev) => !prev);
	};

	const onSubmit: SubmitHandler<LoginInputs> = async (data) => {
		if (isSubmitting) return;

		setIsSubmitting(true);
		setLoading(true);
		setErrorMessage('');

		try {
			const resp = await login({
				email: data.email,
				password: data.password,
			});

			if (resp?.status === 201) {
				const responseData = resp.data;
				localStorage.setItem('accessToken', responseData['accessToken']);

				const userResponse = await whoAmI();
				if (userResponse.status === 200) {
					setUser(userResponse.data);
					localStorage.setItem('user', JSON.stringify(userResponse.data));
					navigate('/courses');
				} else {
					setErrorMessage(userResponse.data?.message || 'Unable to verify user. Please try again.');
				}
			} else {
				setErrorMessage(resp.data?.message || 'Invalid email or password. Please try again.');
			}
		} catch (error) {
			setErrorMessage('An unexpected error occurred. Please try again later.');
			console.error('Login error:', error);
		} finally {
			setLoading(false);
			setIsSubmitting(false);
		}
	};

	const { ref: emailRef, ...emailRegister } = register('email', {
		required: 'Email is required',
		pattern: {
			value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
			message: 'Please enter a valid email address',
		},
	});

	const { ref: passwordRef, ...passwordRegister } = register('password', {
		required: 'Password is required',
		minLength: {
			value: 6,
			message: 'Password must be at least 6 characters',
		},
	});

	const handlePasswordToggleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			toggleShowPassword();
		}
	};

	return (
		<div className='w-full min-h-screen bg-gradient-to-br from-lightBG via-white to-containerGray flex flex-col items-center justify-center p-4 -mt-8'>
			<div className='flex flex-col max-w-[480px] w-full px-6 py-8 mx-auto bg-white rounded-2xl shadow-xl'>
				{/* Logo Section */}
				<div className='flex items-center justify-center mb-8'>
					{/* <CustomLogoIcon width={280} height={90} aria-label='Alkalma Logo' /> */}
					<img src={require('../../assets/images/logo-on-white.png')} alt='Alkalma Logo' width={280} height={90} />
				</div>

				{/* Header */}
				<div className='text-center mb-8'>
					<h1 className='text-[36px] leading-[48px] text-darkViolet font-bold'>Admin Portal</h1>
					<p className='text-greyMedium text-sm mt-2'>Sign in to manage your content</p>
				</div>

				{/* Login Form */}
				<form className='flex flex-col w-full space-y-6' onSubmit={handleSubmit(onSubmit)} noValidate aria-label='Login form'>
					{/* Email Field */}
					<div className='flex flex-col w-full'>
						<label htmlFor='email-input' className='text-base font-semibold text-darkViolet mb-2'>
							Email
						</label>
						<div
							className={`
							relative border-b-2 transition-colors duration-200
							${errors.email ? 'border-redError' : 'border-black200 focus-within:border-primaryViolet'}
							h-[52px] w-full px-4 flex flex-row items-center
						`}
						>
							<input
								id='email-input'
								type='email'
								className='text-base text-darkViolet w-full outline-none placeholder:text-black200 bg-transparent'
								placeholder='Enter your email'
								aria-invalid={errors.email ? 'true' : 'false'}
								aria-describedby={errors.email ? 'email-error' : undefined}
								disabled={loading}
								{...emailRegister}
								ref={(e) => {
									emailRef(e);
									emailInputRef.current = e;
								}}
							/>
						</div>
						{errors.email && (
							<span id='email-error' className='text-redError text-sm mt-1 animate-fade-in' role='alert'>
								{errors.email.message}
							</span>
						)}
					</div>

					{/* Password Field */}
					<div className='flex flex-col w-full'>
						<label htmlFor='password-input' className='text-base font-semibold text-darkViolet mb-2'>
							Password
						</label>
						<div
							className={`
							relative border-b-2 transition-colors duration-200
							${errors.password ? 'border-redError' : 'border-black200 focus-within:border-primaryViolet'}
							h-[52px] w-full px-4 flex flex-row items-center
						`}
						>
							<input
								id='password-input'
								type={showPassword ? 'text' : 'password'}
								className='text-base text-darkViolet w-full outline-none placeholder:text-black200 bg-transparent'
								placeholder='Enter your password'
								aria-invalid={errors.password ? 'true' : 'false'}
								aria-describedby={errors.password ? 'password-error' : undefined}
								disabled={loading}
								{...passwordRegister}
								ref={(e) => {
									passwordRef(e);
									passwordInputRef.current = e;
								}}
								onFocus={() => setErrorMessage('')}
							/>
							<button type='button' className='cursor-pointer p-2 rounded-full hover:bg-greyLight/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primaryViolet' onClick={toggleShowPassword} onKeyDown={handlePasswordToggleKeyDown} aria-label={showPassword ? 'Hide password' : 'Show password'} disabled={loading} tabIndex={0}>
								{!showPassword ? <ShowPasswordIcon className='w-5 h-5 text-greyMedium' /> : <HidePasswordIcon width={20} height={18} className='text-greyMedium' />}
							</button>
						</div>
						{errors.password && (
							<span id='password-error' className='text-redError text-sm mt-1 animate-fade-in' role='alert'>
								{errors.password.message}
							</span>
						)}
					</div>

					{/* General Error Message */}
					{errorMessage && (
						<div className='bg-redError/10 border border-redError rounded-lg p-4 animate-fade-in' role='alert' aria-live='polite'>
							<p className='text-redError text-sm font-medium'>{errorMessage}</p>
						</div>
					)}

					{/* Submit Button */}
					<button
						type='submit'
						className={`
							w-full h-[52px] flex items-center justify-center rounded-full
							bg-primaryViolet text-white text-lg font-semibold
							transition-all duration-200
							${loading || !isValid ? 'opacity-50 cursor-not-allowed' : 'hover:bg-darkViolet hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'}
							focus:outline-none focus:ring-4 focus:ring-primaryViolet/30
						`}
						disabled={loading || !isValid}
						aria-busy={loading}
					>
						{loading ? (
							<div className='flex items-center gap-2'>
								<Loading size={25} />
								<span>Signing in...</span>
							</div>
						) : (
							'Sign in'
						)}
					</button>

					{/* Forgot Password Link */}
					{/* <div className='w-full flex items-center justify-center pt-2'>
						<button
							type='button'
							className='text-base text-primaryViolet font-semibold hover:text-darkViolet hover:underline transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primaryViolet rounded px-2 py-1'
							onClick={() => {
								// TODO: Implement forgot password functionality
								console.log('Forgot password clicked');
							}}
							disabled={loading}
						>
							Forgot password?
						</button>
					</div> */}
				</form>
			</div>
		</div>
	);
}
